import { Navigate } from 'react-router-dom';
import { signInWithPopup } from 'firebase/auth';
import { Network, Users, Share2 } from 'lucide-react';
import { auth, googleProvider } from '@/lib/firebase';
import { useCurrentUser } from '@/lib/use-current-user';
import { toast } from '@/components/ui/sonner';
import { AppHeader, LoadingState } from '@/components/design';

const FEATURES = [
  {
    icon: Network,
    title: '5 форматов',
    body: 'Олимпийка, двойная, круг, швейцарка, группы + плей-офф',
  },
  {
    icon: Users,
    title: 'До 128 участников',
    body: 'Одиночка или пары. Ручная или случайная жеребьёвка',
  },
  {
    icon: Share2,
    title: 'Публичная ссылка',
    body: 'Зрители видят сетку с живым обновлением счёта',
  },
];

function GoogleGlyph() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.4c-.2 1.2-1 2.3-2 3v2.5h3.3c2-1.8 3-4.4 3-7.4z"
        fill="#4285F4"
      />
      <path
        d="M12 22c2.7 0 5-.9 6.7-2.4l-3.3-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3v2.6C4.7 19.9 8.1 22 12 22z"
        fill="#34A853"
      />
      <path
        d="M6.4 14c-.2-.6-.3-1.3-.3-2s.1-1.4.3-2V7.4H3a10 10 0 000 9.2L6.4 14z"
        fill="#FBBC04"
      />
      <path
        d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.9-2.9C16.9 2.9 14.7 2 12 2 8.1 2 4.7 4.1 3 7.4L6.4 10c.8-2.3 3-4.1 5.6-4.1z"
        fill="#EA4335"
      />
    </svg>
  );
}

export function Landing() {
  const user = useCurrentUser();

  async function handleLogin() {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      toast.error('Не получилось войти', {
        description: String((err as Error).message),
      });
    }
  }

  if (user === undefined) {
    return (
      <main className="min-h-screen">
        <AppHeader />
        <LoadingState />
      </main>
    );
  }

  if (user) {
    return <Navigate to="/tournaments" replace />;
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppHeader
        right={
          <button className="btn btn-sm" onClick={handleLogin}>
            Войти
          </button>
        }
      />
      <div
        className="container-d"
        style={{ flex: 1, display: 'grid', placeItems: 'center', padding: '64px 24px' }}
      >
        <div style={{ maxWidth: 640, textAlign: 'center' }}>
          <span className="chip" style={{ marginBottom: 28 }}>
            <span
              style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)' }}
            />
            Платформа для организаторов
          </span>
          <h1 className="td-display" style={{ marginBottom: 18, fontSize: 56 }}>
            Турниры по настольному теннису —{' '}
            <span style={{ color: 'var(--fg-soft)' }}>от жеребьёвки до итоговой таблицы</span>
          </h1>
          <p
            className="t-body"
            style={{
              fontSize: 17,
              color: 'var(--fg-muted)',
              marginBottom: 32,
              maxWidth: 540,
              marginLeft: 'auto',
              marginRight: 'auto',
              lineHeight: 1.55,
            }}
          >
            Создайте турнир, добавьте участников, проведите жеребьёвку и ведите счёт между матчами
            прямо в зале. Зрители смотрят сетку онлайн по короткой ссылке.
          </p>
          <div className="row gap-3" style={{ justifyContent: 'center', marginBottom: 56 }}>
            <button className="btn btn-primary btn-lg" onClick={handleLogin}>
              <GoogleGlyph /> Войти через Google
            </button>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 12,
              marginTop: 28,
              textAlign: 'left',
            }}
          >
            {FEATURES.map((f) => (
              <div key={f.title} className="card-d" style={{ padding: 16 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: 'var(--accent-soft)',
                    color: 'var(--accent-navy)',
                    display: 'grid',
                    placeItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <f.icon size={14} />
                </div>
                <div className="td-h3" style={{ fontSize: 14, marginBottom: 5 }}>
                  {f.title}
                </div>
                <div className="t-xs t-mute" style={{ lineHeight: 1.5 }}>
                  {f.body}
                </div>
              </div>
            ))}
          </div>
          <p className="t-xs t-mute" style={{ marginTop: 32 }}>
            Для организаторов. Зрители смотрят турниры по публичной ссылке без входа.
          </p>
        </div>
      </div>
    </div>
  );
}
